import React, { FC } from "react";
import { useSelector } from "react-redux";

import { IgTypography } from "@intelligems/ig-theme";
import { ensureNumber } from "@intelligems/ig-utils";
import { Grid } from "@mui/material";

import { useConfiguration } from "../../../../api/configurations/use-configuration";
import { useGetExperience } from "../../../../api/experiences/get-experience";
import {
  currencyFormatter,
  noDecimalNumberFormatter,
} from "../../../../api/formatters/dashboard";
import { IAppState } from "../../../../store/types/store";
import {
  BarMetricData,
  HistogramMetricData,
  StackedAreaMetricData,
  StackedBarMetricData,
} from "../../../../types/analytics";
import { getDaysBetweenDates } from "../../../../utils/dateConverters";

import { GraphConfig } from "./graphConfigs";
import { getEstOrders, getEstProfit, getEstRev } from "./graphHelpers";

interface EstImpactProps {
  compareGroupA: string;
  compareGroupB: string;
  config: GraphConfig;
  groupMetricData:
    | BarMetricData[]
    | HistogramMetricData[]
    | StackedBarMetricData[]
    | StackedAreaMetricData[];
  totalTestVisitors: number;
}

export const EstImpact: FC<EstImpactProps> = ({
  compareGroupA,
  compareGroupB,
  config,
  groupMetricData,
  totalTestVisitors,
}) => {
  const dashboardFilters = useSelector(
    (state: IAppState) => state.dashboardFilters
  );
  const experience = useGetExperience(dashboardFilters.experienceId, {
    enabled: Boolean(dashboardFilters.experienceId),
  });

  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");

  const renderEstImpact = (config: GraphConfig) => {
    if (experience.isSuccess) {
      const valA = (groupMetricData as BarMetricData[]).filter(
        (group: BarMetricData) => group.id === compareGroupA
      );
      const valB = (groupMetricData as BarMetricData[]).filter(
        (group: BarMetricData) => group.id === compareGroupB
      );
      const diff = getDaysBetweenDates(
        ensureNumber(dashboardFilters.startTime) ||
          experience.data.experience.startedAtTs ||
          0,
        ensureNumber(dashboardFilters.endTime) ||
          experience.data.experience.endedAtTs ||
          new Date().getTime()
      );
      if (
        valA &&
        valA[0] &&
        valA[0].value &&
        valB &&
        valB[0] &&
        valB[0].value &&
        config.metrics[0]
      ) {
        if (config.metrics[0] === "CVR") {
          const estOrders = getEstOrders(
            valA[0].value,
            valB[0].value,
            totalTestVisitors,
            diff
          );

          return `${estOrders > 0 ? "+" : ""}${noDecimalNumberFormatter(
            estOrders,
            false,
            storeCurrencyConfig!.value!
          )} ORDERS MONTHLY`;
        } else if (config.metrics[0] === "REV_PER_VISITOR") {
          const estRev = getEstRev(
            valA[0].value,
            valB[0].value,
            totalTestVisitors,
            diff
          );

          return `${estRev > 0 ? "+" : ""}${currencyFormatter(
            estRev,
            false,
            storeCurrencyConfig!.value!
          )} MONTHLY`;
        } else if (config.metrics[0] === "PROFIT_PER_VISITOR") {
          const estProfit = getEstProfit(
            valA[0].value,
            valB[0].value,
            totalTestVisitors,
            diff
          );

          return `${estProfit > 0 ? "+" : ""}${currencyFormatter(
            estProfit,
            false,
            storeCurrencyConfig!.value!
          )} MONTHLY`;
        }
      }
    }
  };

  return (
    <Grid item sx={{ marginBottom: "15px" }}>
      <IgTypography>EST. {renderEstImpact(config)}</IgTypography>
    </Grid>
  );
};
